// The Agents page's change-history read (#5208 wire, #5209 client) —
// `GET /v1/agents/history?agent=<id>`: who last changed one agent's model pin or
// on/off switch, and its 5-entry timeline. Parsed through the domain's own
// `AgentHistoryEventWireSchema` (unlike the preview, this wire already has a
// published resource — `@developerz/domain` `wire/agent-history.ts` — so this
// file is a thin client, not a mirror).
//
// One hook per agent, matching `ChangedByChip`'s own scope: the chip renders one
// agent's timeline, and each card mounts its own chip — polling would just repeat
// the same 5 rows every agent already fetched once, so this is a plain query
// (like `useLlmRoutes`), not polled. It is read EAGERLY, at mount: the chip's
// trigger shows the latest row, so it cannot wait for a tap. A write the chip
// names re-reads it by invalidating {@link AGENTS_HISTORY_QUERY_KEY}: the model
// save (`agent-model-save.ts`) and the mode and repo-pin saves (`routes/Agents.tsx`).

import {
  type AgentHistoryEventWire,
  AgentHistoryEventWireSchema,
  type AgentId,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

export type { AgentHistoryEventWire };

export const AGENTS_HISTORY_QUERY_KEY = 'agents_history';

/** The page envelope, aliased (not inline) so `wire-parse.guard.test.ts` follows
 * the one hop from `{ schema: agentHistoryPageSchema }` to `AgentHistoryEventWireSchema` —
 * the same idiom `lib/ci.ts`'s `ciRunsSchema` uses for its own paged family. */
const agentHistoryPageSchema = z.object({ data: z.array(AgentHistoryEventWireSchema) });

/** One agent's first page of change history (5 rows, the server's own default). */
export async function fetchAgentHistory(
  agent: AgentId,
  token: string | null,
  signal?: AbortSignal,
): Promise<readonly AgentHistoryEventWire[]> {
  return (
    await apiFetch(
      `/v1/agents/history?agent=${encodeURIComponent(agent)}`,
      token,
      signal ? { signal } : undefined,
      { schema: agentHistoryPageSchema },
    )
  ).data;
}

/**
 * One agent's change timeline. Idle with no session AND while `enabled` is
 * false; `ChangedByChip` passes `true` (see the file header).
 */
export function useAgentHistory(
  agent: AgentId,
  enabled: boolean,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<readonly AgentHistoryEventWire[]>(() => ({
    queryKey: [AGENTS_HISTORY_QUERY_KEY, agent, token, activeAccountId()],
    enabled: enabled && token !== null,
    queryFn: ({ signal }) => fetchAgentHistory(agent, token, signal),
  }));
}
